import { SET_USER, SET_UNAUTHENTICATED, DELETED_SAVED_LIST } from '../types';
import { sortSavedPreviews, updateSavedPreviewPages } from '../../utils/savedPreviewCache';

const emptySavedHighlights = {
    uid: '', session: 0, memberships: {}, records: {}, pages: {}, pending: {}, revision: 0,
    mutations: {}, notice: null, revisionById: {}, requests: {}, errors: {}, failedCursors: {}, selection: null, deletedListIds: {},
};
export default function savedPreviewReducer(state = emptySavedHighlights, action) {
    const p = action.payload || {};
    if (action.type === 'OPEN_SAVED_HIGHLIGHT') return { ...state, selection: { preview: p.preview, token: p.token, shouldResume: Boolean(p.shouldResume) } };
    if (action.type === 'CLOSE_SAVED_HIGHLIGHT') return state.selection?.token === p.token ? { ...state, selection: null } : state;
    if (action.type === SET_UNAUTHENTICATED) return { ...emptySavedHighlights, session: state.session + 1 };
    if (action.type === SET_USER) {
        const same = state.uid === p.uid;
        const base = same ? state : { ...emptySavedHighlights, session: state.session + 1 };
        const memberships = Object.fromEntries(Object.entries(p.savedPreviewIdToSavedListIds || {}).map(([id, lists]) => [id, lists.filter(listId => !base.deletedListIds[listId])]));
        // Account bootstrap can finish after a local mutation. Keep the newer intent.
        Object.keys(base.revisionById).forEach(id => {
            if (!base.mutations[id] && base.revisionById[id] <= (action.meta?.savedHighlightStartedRevision ?? base.revision)) return;
            if (Object.prototype.hasOwnProperty.call(base.memberships, id)) memberships[id] = base.memberships[id];
            else delete memberships[id];
        });
        const reconcile = records => records.filter(record => Object.prototype.hasOwnProperty.call(memberships, record.preview.id))
            .map(record => ({ ...record, savedListIds: memberships[record.preview.id] }));
        const pages = Object.fromEntries(Object.entries(base.pages).map(([filter, page]) => [filter, { ...page,
            records: reconcile(page.records).filter(record => filter === '__all__' || record.savedListIds.includes(filter)),
        }]));
        const records = Object.fromEntries(reconcile(Object.values(base.records)).map(record => [record.preview.id, record]));
        return { ...base, uid: p.uid || '', memberships, records, pages, selection: same || !state.uid ? state.selection : null };
    }
    if (action.type === DELETED_SAVED_LIST) {
        const memberships = Object.fromEntries(Object.entries(state.memberships).map(([id, lists]) => [id, lists.filter(list => list !== p.id)]));
        const records = Object.fromEntries(Object.entries(state.records).map(([id, record]) => [id, { ...record, savedListIds: record.savedListIds.filter(list => list !== p.id) }]));
        const pages = Object.fromEntries(Object.entries(state.pages).filter(([filter]) => filter !== p.id)
            .map(([filter, page]) => [filter, { ...page, records: page.records.map(record => ({ ...record, savedListIds: record.savedListIds.filter(list => list !== p.id) })) }]));
        return { ...state, memberships, records, pages, revision: state.revision + 1, deletedListIds: { ...state.deletedListIds, [p.id]: true } };
    }
    if (!action.type.startsWith('SAVED_HIGHLIGHTS_') || p.uid !== state.uid || p.session !== state.session) return state;
    switch (action.type) {
        case 'SAVED_HIGHLIGHTS_CACHE': {
            const pages = Object.fromEntries(Object.entries(p.pages).filter(([filter]) => !state.deletedListIds[filter]).map(([filter, page]) => [filter, {
                ...page, records: page.records.filter(record => Object.prototype.hasOwnProperty.call(state.memberships, record.preview.id))
                    .map(record => ({ ...record, savedListIds: state.memberships[record.preview.id] }))
                    .filter(record => filter === '__all__' || record.savedListIds.includes(filter)),
            }]));
            const records = Object.fromEntries(Object.values(pages).flatMap(page => page.records.map(record => [record.preview.id, record])));
            return { ...state, pages: { ...pages, ...state.pages }, records: { ...records, ...state.records } };
        }
        case 'SAVED_HIGHLIGHTS_MUTATION': {
            const pending = { ...state.pending }; const mutations = { ...state.mutations };
            if (p.mutation?.pending) pending[p.id] = p.token; else delete pending[p.id];
            if (p.mutation) mutations[p.id] = p.mutation; else delete mutations[p.id];
            const memberships = { ...state.memberships }; const records = { ...state.records };
            if (p.record) { memberships[p.id] = p.record.savedListIds; records[p.id] = p.record; }
            else { delete memberships[p.id]; delete records[p.id]; }
            const revision = state.revision + 1;
            return { ...state, memberships, records, pending, mutations, revision,
                revisionById: { ...state.revisionById, [p.id]: revision },
                pages: updateSavedPreviewPages(state.pages, p.id, p.record) };
        }
        case 'SAVED_HIGHLIGHTS_NOTICE':
            return { ...state, notice: p.notice };
        case 'SAVED_HIGHLIGHTS_LOADING':
            return { ...state, requests: { ...state.requests, [p.filter]: p.request }, errors: { ...state.errors, [p.filter]: null },
                failedCursors: { ...state.failedCursors, [p.filter]: null } };
        case 'SAVED_HIGHLIGHTS_LOADED': {
            if (state.requests[p.filter] !== p.request || state.deletedListIds[p.filter]) return state;
            const byId = Object.fromEntries((p.cursor ? state.pages[p.filter]?.records || [] : []).map(record => [record.preview.id, record]));
            const memberships = { ...state.memberships }; const records = { ...state.records };
            let pages = state.pages;
            p.records.forEach(record => {
                const id = record.preview.id;
                if (state.mutations[id] || (state.revisionById[id] || 0) > p.revision) return;
                memberships[id] = record.savedListIds; records[id] = record;
                pages = updateSavedPreviewPages(pages, id, record);
                if (p.filter === '__all__' || record.savedListIds.includes(p.filter)) byId[id] = record;
            });
            // A complete response also confirms removals made on another device.
            // Reconcile every loaded filter so its offline view agrees with All.
            if (!p.cursor && !p.nextCursor) {
                const returned = new Set(p.records.map(record => record.preview.id));
                Object.keys(memberships).forEach(id => {
                    if (returned.has(id) || state.mutations[id] || (state.revisionById[id] || 0) > p.revision) return;
                    if (p.filter === '__all__') {
                        delete memberships[id]; delete records[id];
                        pages = updateSavedPreviewPages(pages, id, null);
                    } else if (memberships[id].includes(p.filter)) {
                        memberships[id] = memberships[id].filter(listId => listId !== p.filter);
                        if (records[id]) {
                            records[id] = { ...records[id], savedListIds: memberships[id] };
                            pages = updateSavedPreviewPages(pages, id, records[id]);
                        }
                    }
                });
            }
            // Preserve successful mutations newer than the request; never resurrect an unsave.
            Object.values(state.records).forEach(record => {
                const id = record.preview.id;
                if ((state.mutations[id] || (state.revisionById[id] || 0) > p.revision) && (p.filter === '__all__' || record.savedListIds.includes(p.filter))) byId[id] = record;
            });
            Object.keys(byId).forEach(id => {
                if ((state.revisionById[id] || 0) > p.revision && (!memberships[id] || (p.filter !== '__all__' && !memberships[id].includes(p.filter)))) delete byId[id];
            });
            return { ...state, memberships, records, requests: { ...state.requests, [p.filter]: null },
                failedCursors: { ...state.failedCursors, [p.filter]: null },
                pages: { ...pages, [p.filter]: { records: sortSavedPreviews(Object.values(byId)), nextCursor: p.nextCursor || null } } };
        }
        case 'SAVED_HIGHLIGHTS_FAILED':
            if (state.requests[p.filter] !== p.request) return state;
            return { ...state, requests: { ...state.requests, [p.filter]: null }, errors: { ...state.errors, [p.filter]: p.error },
                failedCursors: { ...state.failedCursors, [p.filter]: p.cursor || null } };
        default: return state;
    }
}
